import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import type { EligibleQuotaInvoice, QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import { getQuotaRequiredCents } from "~/modules/quota/utils/quotaFormUtils.ts";

// The net excess of an attached invoice. The quota amount is the frozen sum of these values.
export function getAttachedInvoiceExcessCents(invoice: Pick<Invoice, "subtotalCents" | "totalTrackedCents">): number {
	return invoice.subtotalCents - invoice.totalTrackedCents;
}

// The quota amount after the detach of the invoice. The backend recomputes it from the invoices that stay.
export function getAmountCentsAfterDetach(quota: QuotaWithRelations, invoiceId: string): number {
	return quota.invoices
		.filter((invoice) => invoice.id !== invoiceId)
		.reduce((sum, invoice) => sum + getAttachedInvoiceExcessCents(invoice), 0);
}

// The same two refusals as the detach request (backend ticket 22). Null when the detach is possible.
// The 422 of the request stays the definite answer.
export function getInvoiceDetachLockMessage(quota: QuotaWithRelations, invoiceId: string): string | null {
	const isLastInvoice = quota.invoices.length === 1;
	if (isLastInvoice && quota.consumers.length > 0) {
		return "Die letzte Rechnung kann erst entfernt werden, wenn keine Bestellungen mehr zugewiesen sind.";
	}

	if (getAmountCentsAfterDetach(quota, invoiceId) < getQuotaRequiredCents(quota.consumers)) {
		return "Die Rechnung kann nicht entfernt werden, da das verbleibende Budget die Bestellungen nicht deckt.";
	}

	return null;
}

// "Title (Number)" for the project of an eligible invoice. The number is left out when it is empty.
export function formatQuotaInvoiceProjectLabel(invoice: EligibleQuotaInvoice): string {
	const title = invoice.projectTitle ?? "Ohne Projekttitel";
	return invoice.projectNumber ? `${title} (${invoice.projectNumber})` : title;
}
